import { Component, computed, effect, input, resource, signal } from '@angular/core';
import { TranslatePipe } from '@cmi/addin-core';
import { updateContentControl } from '../../lib/word-content-controls';

@Component({
  selector: 'cmi-content-control-editor',
  imports: [TranslatePipe],
  templateUrl: './content-control-editor.html'
})
export class ContentControlEditor {
  public readonly id = input.required<number>();
  public readonly refreshTrigger = input<number>(0);

  public readonly titleSignal = signal<string>('');
  public readonly tagSignal = signal<string>('');

  private readonly _fetchResource = resource<void, number>({
    params: () => this.id(),
    loader: async ({ params }) => {
      await Word.run(async (context) => {
        const contentControl = context.document.contentControls.getByIdOrNullObject(params);
        contentControl.load(['title', 'tag', 'isNullObject']);
        await context.sync();

        if (contentControl.isNullObject) {
          return;
        }

        this.titleSignal.set(contentControl.title);
        this.tagSignal.set(contentControl.tag);
      });
    }
  });

  // Access the resource to ensure it runs
  public readonly isLoading = computed(() => this._fetchResource.isLoading());

  constructor() {
    // Watch refreshTrigger and reload resource when it changes
    // Skip initial value (0) to avoid unnecessary reload on mount
    effect(() => {
      const trigger = this.refreshTrigger();
      if (trigger > 0) {
        this._fetchResource.reload();
      }
    });
  }

  private readonly _isSaving = signal<boolean>(false);
  private readonly _saveError = signal<Error | null>(null);

  public readonly isSaving = computed(() => this._isSaving());
  public readonly saveError = computed(() => this._saveError());

  public onTitleChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.titleSignal.set(target.value);
  }

  public onTagChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.tagSignal.set(target.value);
  }

  public async onSaveTitleAndTag(): Promise<void> {
    const id = this.id();
    const title = this.titleSignal();
    const tag = this.tagSignal();

    if (!id) {
      return;
    }

    this._isSaving.set(true);
    this._saveError.set(null);

    try {
      await updateContentControl(id, title, tag);
    } catch (error) {
      console.error('Failed to save content control:', error);
      this._saveError.set(error instanceof Error ? error : new Error(String(error)));
    } finally {
      this._isSaving.set(false);
    }
  }
}
